import type { PageBlock } from "@/utils/customPageStore";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import {
  SectionAdminBar,
  useBlockConfig,
  ArrayDeleteButton,
  AddItemButton,
} from "./shared";

interface FounderStoryBlockProps {
  block: PageBlock;
  isAdmin: boolean;
  isDark: boolean;
  headingFont: string;
  headingStyle: React.CSSProperties;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  onUpdate?: (b: PageBlock) => void;
}

export default function FounderStoryBlock({
  block,
  isAdmin,
  isDark,
  headingFont,
  headingStyle,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onUpdate,
}: FounderStoryBlockProps) {
  const { get, setConfig } = useBlockConfig(block, onUpdate);
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-600";

  const eyebrow = get("eyebrow", "");
  const heading = get("heading", "");
  const founderStory = get("founderStory", "");
  const founderName = get("founderName", "");
  const founderTitle = get("founderTitle", "");
  const founderImage = get("founderImage", "");
  const missionStatement = get("missionStatement", "");
  const pillars = get("pillars", []) as { icon: string; title: string; body: string }[];

  const setPillar = (idx: number, key: "icon" | "title" | "body", val: string) => {
    const newPillars = pillars.map((p, i) => (i === idx ? { ...p, [key]: val } : p));
    setConfig("pillars", newPillars);
  };

  const deletePillar = (idx: number) => {
    setConfig("pillars", pillars.filter((_, i) => i !== idx));
  };

  const addPillar = () => {
    setConfig("pillars", [...pillars, { icon: "ri-star-line", title: "New Pillar", body: "Description here..." }]);
  };

  return (
    <div className={`mb-8 ${isAdmin ? "rounded-xl overflow-hidden border border-green-200" : ""}`}>
      {isAdmin && (
        <SectionAdminBar
          layout="founder-story"
          isDark={isDark}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          isFirst={isFirst}
          isLast={isLast}
        />
      )}
      <div className="py-12 px-6 rounded-2xl" style={{ background: isDark ? "#1f2937" : "#fff" }}>
        <div className="text-center mb-10">
          {eyebrow && (
            <AdminEditField value={eyebrow} onSave={(v) => setConfig("eyebrow", v)} inputClassName="text-center text-sm">
              <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{eyebrow}</span>
            </AdminEditField>
          )}
          {heading && (
            <AdminEditField value={heading} onSave={(v) => setConfig("heading", v)} inputClassName="text-center text-3xl md:text-4xl font-extrabold">
              <h2 className={`text-3xl md:text-4xl font-extrabold mt-3 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                {heading}
              </h2>
            </AdminEditField>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-10 mb-12">
          <div className="w-full lg:w-2/5 flex-shrink-0 space-y-5">
            {founderImage && (
              <div className="w-full h-80 rounded-3xl overflow-hidden relative">
                {isAdmin && onUpdate ? (
                  <AdminImageUpload
                    src={founderImage || ""}
                    alt={founderName}
                    onReplace={(url) => setConfig("founderImage", url)}
                    className="w-full h-full"
                    imgClassName="w-full h-full object-cover object-top"
                    isDark={isDark}
                  />
                ) : (
                  <img src={founderImage} alt={founderName} className="w-full h-full object-cover object-top" />
                )}
                <div className="absolute bottom-0 left-0 right-0 p-5" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.55), transparent)" }}>
                  <div className="text-white font-bold">{founderName}</div>
                  <div className="text-white/70 text-sm">{founderTitle}</div>
                </div>
              </div>
            )}
            {missionStatement && (
              <AdminEditField value={missionStatement} onSave={(v) => setConfig("missionStatement", v)} multiline inputClassName="text-sm">
                <div className="rounded-2xl p-5" style={{ background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" }}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-pink-400 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-heart-line text-white text-sm"></i>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-pink-500 mb-2">Our Mission</div>
                      <p className={`font-medium text-sm italic leading-relaxed ${isDark ? "text-white" : "text-gray-800"}`}>
                        &ldquo;{missionStatement}&rdquo;
                      </p>
                    </div>
                  </div>
                </div>
              </AdminEditField>
            )}
          </div>
          <div className="w-full lg:w-3/5">
            <div className="w-12 h-1 rounded-full bg-pink-400 mb-5"></div>
            {founderStory && (
              <AdminEditField value={founderStory} onSave={(v) => setConfig("founderStory", v)} multiline inputClassName="text-base leading-relaxed">
                <p className={`leading-relaxed text-base whitespace-pre-line ${subColor}`}>{founderStory}</p>
              </AdminEditField>
            )}
          </div>
        </div>

        {pillars.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map((pillar, i) => (
              <div
                key={i}
                className={`rounded-2xl p-5 border relative group/pillar ${
                  isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100"
                }`}
              >
                {isAdmin && onUpdate && <ArrayDeleteButton onDelete={() => deletePillar(i)} />}
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-3 ${isDark ? "bg-pink-900/40" : "bg-pink-50"}`}>
                  {isAdmin && onUpdate ? (
                    <div className="flex flex-col items-center">
                      <i className={`${pillar.icon} text-pink-500 text-xl`}></i>
                      <AdminEditField
                        value={pillar.icon}
                        onSave={(v) => setPillar(i, "icon", v)}
                        inputClassName="text-[8px] w-full text-center"
                      >
                        <span className="text-[8px] text-pink-400">{pillar.icon}</span>
                      </AdminEditField>
                    </div>
                  ) : (
                    <i className={`${pillar.icon} text-pink-500 text-xl`}></i>
                  )}
                </div>
                <AdminEditField
                  value={pillar.title}
                  onSave={(v) => setPillar(i, "title", v)}
                  inputClassName="font-bold text-sm mb-2"
                >
                  <h4 className={`font-bold text-sm mb-2 ${headingColor}`}>{pillar.title}</h4>
                </AdminEditField>
                <AdminEditField
                  value={pillar.body}
                  onSave={(v) => setPillar(i, "body", v)}
                  multiline
                  inputClassName="text-sm leading-relaxed"
                >
                  <p className={`text-sm leading-relaxed ${subColor}`}>{pillar.body}</p>
                </AdminEditField>
              </div>
            ))}
            {isAdmin && onUpdate && <AddItemButton onClick={addPillar} label="Add Pillar" isDark={isDark} className="p-5" />}
          </div>
        )}
      </div>
    </div>
  );
}